import React, { useContext, useEffect, useState, useRef, useCallback } from 'react';
import { Button, Spinner } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft, faSave, faTimes, faInfoCircle, faPlus, faTrash, faChevronDown, faChevronUp, faDesktop, faGavel, faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './RuleEditor.css';
import DetailSkeleton from '../../../../components/Skeleton/DetailSkeleton';
import { ProductContext } from '../../../../store';
import { PostData } from '../../../../services/FetchData';
import AsyncCreatableSelect from 'react-select/async-creatable';
import AsyncSelect from "react-select/async";
import debounce from 'lodash.debounce';

const RuleEditor = () => {
	const navigate = useNavigate();
	const { id: ruleId } = useParams();
	const isEditMode = !!ruleId;
	
	const [expandedSections, setExpandedSections] = useState({
		scope: true,
		action: true,
		dates: true,
		conditions: true
	});

	const [showPreview, setShowPreview] = useState(false);
	const [saveLoader, setSaveLoader]	= useState(false);
	const [pageLoader, setPageLoader] = useState(isEditMode);
	const [searchResults, setSearchResults] = useState(true);
	const [smartCollections, setSmartCollections] = useState(true);
	const [scopeSelection, setScopeSelection] = useState('allQueries');
	const [collectionsSelection, setCollectionsSelection] = useState('allCollections');
	const [selectedQueries, setSelectedQueries] = useState([]);
	const [selectedCollections, setSelectedCollections] = useState([]);
	const [actionType, setActionType] = useState({ value: 'promote', label: 'Promote' });
	const [actionValue, setActionValue] = useState({ value: '1', label: '1' });
	const [selectedOuterLogic, setSelectedOuterLogic] = useState(null); // Track selected logic for outer AND/OR buttons
	const [startDate, setStartDate] = useState(null);
	const [endDate, setEndDate] = useState(null);
	const [redirectUrl, setRedirectUrl] = useState('');
	// Store View (Magento only)
	const [storeView, setStoreView] = useState(null);
	const [storeViewOptions, setStoreViewOptions] = useState([]);
	const {
		currentWebsiteId,
		websitePlatform,
		magentoStoreViews,
		magentoStoreViewsLoader,
		defaultStoreView,
	} = useContext(ProductContext);

	const isMagento = String(websitePlatform || '').toLowerCase() === 'magento';

	// Build store view options from `getStoreViews` (loaded in ProductProvider)
	useEffect(() => {
		if (!isMagento) {
			setStoreView(null);
			setStoreViewOptions([]);
			return;
		}

		const options = Object.keys(magentoStoreViews || {}).map((key) => ({
			value: key,
			label: magentoStoreViews?.[key] || key,
		}));
		setStoreViewOptions(options);

		// Keep current selection if still valid; otherwise default to `defaultStoreView` or first option.
		const hasCurrent = storeView?.value && options.some((o) => o.value === storeView.value);
		if (hasCurrent) return;

		const preferred =
			(options.find((o) => o.value === defaultStoreView) || options[0]) ?? null;
		setStoreView(preferred);
	}, [isMagento, magentoStoreViews, defaultStoreView]);

	// Load existing rule data for edit mode
	const loadRuleData = useCallback(async () => {
		if (!isEditMode || !currentWebsiteId) return;
		
		setPageLoader(true);
		try {
			const response = await PostData("ms1", "api/v1/rule/getRule", {
				websiteId: currentWebsiteId,
				ruleId: ruleId
			});
			
				if (response !== "Invalid" && response !== undefined && response && response.status === "success" && response.data && response.data.rule) {
				const rule = response.data.rule;
				
				// Load scope config
					setSearchResults(rule.scopeConfig?.searchResults || false);
					setSmartCollections(rule.scopeConfig?.smartCollections || false);
					setScopeSelection(rule.scopeConfig?.scopeSelection || 'allQueries');
					setCollectionsSelection(rule.scopeConfig?.collectionsSelection || 'allCollections');
					setSelectedQueries(Array.isArray(rule.scopeConfig?.selectedQueries) ? rule.scopeConfig.selectedQueries : []);
					setSelectedCollections(Array.isArray(rule.scopeConfig?.selectedCollections) ? rule.scopeConfig.selectedCollections : []);
					setActionType({
						value: rule.actionConfig?.type || 'Pin',
						label: rule.actionConfig?.type ? (rule.actionConfig.type.charAt(0).toUpperCase() + rule.actionConfig.type.slice(1)) : 'Pin'
					});
					setActionValue({
						value: rule.actionConfig?.value || '',
						label: rule.actionConfig?.value || ''
					});

					// Normalize facet-field values into react-select format for edit mode display.
					// Before normalization, enrich field objects with facetField
					const normalizedConditions = Array.isArray(rule.conditions)
						? rule.conditions.map((group) =>
								Array.isArray(group)
									? group.map((cond) => {
											// Check if this is a Price field (handle variations like "Price", "Retail Price", etc.)
											const fieldValue = cond?.field?.value || '';
											const fieldLabel = cond?.field?.label || '';
											const isPriceField = fieldValue === 'Price' || 
																 fieldLabel === 'Price' ||
																 fieldValue.toLowerCase().includes('price') ||
																 fieldLabel.toLowerCase().includes('price');
											
											// Convert old 'from'/'to' operators to 'range' for Price fields
											let normalizedOperator = cond?.operator;
											if (isPriceField && (cond?.operator?.value === 'from' || cond?.operator?.value === 'to')) {
												normalizedOperator = { value: 'range', label: 'Range' };
											}
											
											// Find matching field option to restore facetField
											// For Price fields, also check if field name contains 'price'
											let fieldOption = conditionFieldOptions.find(
												opt => opt.value === cond?.field?.value || 
													   opt.label === cond?.field?.label
											);
											
											// If Price field not found, use the static Price option
											if (isPriceField && !fieldOption) {
												fieldOption = conditionFieldOptions.find(opt => opt.value === 'Price');
											}
											
											// Determine facetField:
											// 1. Use from API response (cond.field?.facetField) if available - PRIORITY
											// 2. Use from matched option (fieldOption?.facetField) if available
											// 3. If values is array, infer from field.value (lowercase)
											// 4. Otherwise no facetField
											let facetField = cond.field?.facetField || fieldOption?.facetField;
											
											if (!facetField && Array.isArray(cond.values) && cond.values.length > 0) {
												// Infer facetField from field value for any multi-value field
												facetField = String(fieldValue || '').toLowerCase();
											}
											
											// For Price field, preserve original field value/label and ensure facetField is set ONLY if it's Price (Values)
											let enrichedField = cond.field;
											if (isPriceField) {
												const hasValuesArray = Array.isArray(cond.values) && cond.values.length > 0;
												const hasMinMaxValue = cond.minValue || cond.maxValue;
												
												// Price (Values) = has values array, no minValue/maxValue
												// Static Price = has minValue/maxValue, no values array
												if (hasValuesArray && !hasMinMaxValue) {
													// This is Price (Values) - ensure facetField is set
													// Prioritize: API facetField > inferred facetField > default 'price'
													const priceFacetField = cond.field?.facetField || facetField || 'price';
													enrichedField = {
														...cond.field,
														facetField: priceFacetField,
														value: cond.field?.value || fieldOption?.value,
														label: cond.field?.label || fieldOption?.label
													};
													// Ensure facetField variable is set for normalization logic below
													facetField = priceFacetField;
												} else if (hasMinMaxValue) {
													// Static Price field - preserve as-is, explicitly don't add facetField
													enrichedField = {
														...cond.field,
														facetField: undefined, // Explicitly remove facetField for static Price
														value: cond.field?.value || fieldOption?.value,
														label: cond.field?.label || fieldOption?.label
													};
													// Clear facetField for static Price to prevent normalization
													facetField = null;
												} else if (fieldOption) {
													// Fallback case - preserve field as-is, but don't lose API facetField if present
													enrichedField = {
														...cond.field,
														// Preserve API facetField if it exists, otherwise don't add one
														facetField: cond.field?.facetField,
														value: cond.field?.value || fieldOption.value,
														label: cond.field?.label || fieldOption.label
													};
													// If API has facetField, ensure it's set for normalization
													if (cond.field?.facetField) {
														facetField = cond.field.facetField;
													}
												} else {
													// No fieldOption found - preserve API field as-is
													enrichedField = cond.field;
													// If API has facetField, ensure it's set for normalization
													if (cond.field?.facetField) {
														facetField = cond.field.facetField;
													}
												}
											} else if (facetField) {
												// Attach facetField if determined (for non-Price fields)
												enrichedField = { ...cond.field, facetField: facetField };
											}
											
											// Build normalized condition with all original properties preserved
											const normalizedCond = {
												...cond,
												field: enrichedField,
												operator: normalizedOperator || cond.operator
											};
											
											// If no facetField, return with enriched field and all original properties (including minValue/maxValue)
											if (!facetField) {
												return normalizedCond;
											}
											
											// Normalize values for facet fields
											if (!Array.isArray(cond.values)) {
												return normalizedCond;
											}
											
											return {
												...normalizedCond,
												values: cond.values
													.map((x) => normalizeFacetOption(x, facetField))
													.filter(Boolean)
											};
										})
									: group
						  )
						: rule.conditions;

					setConditions(normalizedConditions);
					
					// Load date fields if they exist
					if (rule.date && rule.date.startDate) {
						setStartDate(new Date(rule.date.startDate));
					}
					if (rule.date && rule.date.endDate) {
						setEndDate(new Date(rule.date.endDate));
					}

					// Load redirect URL if it exists and action type is Redirect
					if (rule.actionConfig && rule.actionConfig.type === 'Redirect' && rule.actionConfig.value) {
						setRedirectUrl(rule.actionConfig.value);
					}

			} else {
				toast.error(response.message || 'Failed to load rule data');
				navigate('/search/merchandising/rules');
			}
		} catch (error) {
			console.error('Error loading rule data:', error);
			toast.error('Failed to load rule data');
			navigate('/search/merchandising/rules');
		} finally {
			setPageLoader(false);
		}
	}, [isEditMode, currentWebsiteId, ruleId, navigate]);

	useEffect(() => {
		if (isEditMode && currentWebsiteId) {
			loadRuleData();
		}
	}, [isEditMode, currentWebsiteId, ruleId, loadRuleData]);

	// --- Dynamic condition fields (key -> shown in field dropdown, value -> used to fetch values) ---
	const staticSpecialFields = [
		{ value: 'Price', label: 'Price' },
		{ value: 'Created at', label: 'Created at' },
	];

	const [conditionFieldOptions, setConditionFieldOptions] = useState([...staticSpecialFields]);
	const [facetFieldsMap, setFacetFieldsMap] = useState({});

	const normalizeFacetOption = (item, facetField) => {
		if (item == null) return null;

		// Already in react-select format
		if (typeof item === 'object' && item.value != null && item.label != null) {
			return { value: item.value, label: item.label };
		}

		// Primitive value
		if (typeof item !== 'object') {
			return { value: item, label: String(item) };
		}

		// Explicit mapping requested for Brand/Categories/Collections
		if (facetField === 'brand') {
			return {
				value: item.brandId ?? item.brandId ?? item.id ?? item.value ?? item.brandId,
				label: item.brand ?? item.label ?? item.name ?? String(item.brand ?? item.brand ?? item.id ?? '')
			};
		}
		if (facetField === 'categories') {
			return {
				value: item.categoryId ?? item.categoryid ?? item.id ?? item.value ?? item.category,
				label: item.category ?? item.label ?? item.name ?? String(item.categoryId ?? item.categoryid ?? item.id ?? '')
			};
		}
		if (facetField === 'collections') {
			return {
				value: item.collectionId ?? item.collectionid ?? item.id ?? item.value ?? item.collection,
				label: item.collection ?? item.label ?? item.name ?? String(item.collectionId ?? item.collectionid ?? item.id ?? '')
			};
		}

		// Best-effort fallback
		if (item.id != null && item.name != null) return { value: item.id, label: item.name };
		// Common API shape: { key: <label>, value: <id> }
		if (item.key != null && item.value != null) return { value: item.value, label: String(item.key) };

		const value = item.value ?? item.id ?? item.name ?? JSON.stringify(item);
		const label = item.label ?? item.name ?? String(value);
		return { value, label };
	};

	const prettyConditionFieldLabel = (rawKey) => {
		if (!rawKey) return '';
		const key = String(rawKey);

		const overrides = {
			collection_ids: 'Collection Ids',
			category_ids: 'Category Ids',
			brandId: 'Brand Id',
			price: 'Price (Values)',
		};
		if (overrides[key]) return overrides[key];

		// snake_case / kebab-case -> words, camelCase -> words, trim trailing _ids
		let cleaned = key.replace(/_ids$/i, '');
		cleaned = cleaned
			.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
			.replace(/[_-]+/g, ' ')
			.trim();

		// Title Case
		return cleaned
			.split(' ')
			.filter(Boolean)
			.map((w) => w.charAt(0).toUpperCase() + w.slice(1))
			.join(' ');
	};

	const normalizeConditionFieldOption = (item) => {
		if (item == null) return null;

		// Already in react-select format, with an attached facetField
		if (typeof item === 'object' && item.value != null && item.label != null) {
			return item.facetField != null
				? { value: item.value, label: item.label, facetField: item.facetField }
				: { value: item.value, label: item.label };
		}

		// Expected API shape: { key: 'Brand', value: 'brand' }
		if (typeof item === 'object' && item.key != null && item.value != null) {
			const keyStr = String(item.key);
			const valueStr = String(item.value);

			// If API sends { key: 'brand', value: 'brandId' }, prefer the key as facetField
			// so selecting "brand" does not fetch brandId values.
			const keyLower = keyStr.toLowerCase();
			const preferredFacetFields = new Set(['brand', 'categories', 'collections']);
			const facetField = preferredFacetFields.has(keyLower) ? keyLower : valueStr;

			return { value: item.key, label: prettyConditionFieldLabel(item.key), facetField };
		}

		return null;
	};

	useEffect(() => {
		if (!currentWebsiteId) return;
		let active = true;

		(async () => {
			try {
				// This endpoint is used in two modes:
				// - no facetField: return available facet fields (key/value)
				// - facetField provided: return values for that facet field
				const response = await PostData("ms1", "api/v1/typesense/getSearchFacetField", {
					websiteId: currentWebsiteId,
					...(isMagento && (storeView?.value || defaultStoreView)
						? { storeView: storeView?.value || defaultStoreView }
						: {}),
				});

				if (!active) return;
				if (response === "Invalid" || response?.status !== "success") return;

				const facetFields =
					response?.data?.facetFields ??
					response?.data?.fields ??
					response?.data?.searchResult ??
					response?.data?.result ??
					response?.data ??
					[];

				let dynamic = [];
				if (Array.isArray(facetFields)) {
					// Mode A: API returns available facet fields in [{ key, value }] format
					dynamic = facetFields.map(normalizeConditionFieldOption).filter(Boolean);
				} else if (facetFields && typeof facetFields === 'object') {
					const entries = Object.entries(facetFields);
					const isFacetValuesMap = entries.length > 0 && entries.every(([, v]) => Array.isArray(v));

					if (isFacetValuesMap) {
						const excludedFacetFieldsLower = new Set(['brandid', 'category_ids', 'collection_ids', 'collections_ids']);
						const filteredEntries = entries.filter(([facetField]) => !excludedFacetFieldsLower.has(String(facetField).toLowerCase()));

						// Mode B: API returns a map of facetField -> values[]
						setFacetFieldsMap(facetFields);
						dynamic = filteredEntries.map(([facetField]) => ({
							value: facetField,
							label: prettyConditionFieldLabel(facetField),
							facetField: facetField
						}));
					} else {
						// Mode C: API returns an object mapping: { Brand: 'brand', Categories: 'categories' }
						dynamic = entries
							.map(([key, value]) => normalizeConditionFieldOption({ key, value }))
							.filter(Boolean);
					}
				}

				if (!dynamic.length) return;

				// Keep Price/Created-at available even if not returned by API
				const merged = [...dynamic, ...staticSpecialFields];

				// Deduplicate by *display label* for facet fields to avoid showing
				// e.g. both "Categories" and "category_ids" (both prettified to "Categories").
				// Prefer the option whose original key already looks friendly (value === label).
				const byId = new Map();
				for (const opt of merged) {
					const id = opt?.facetField ? String(opt.label).toLowerCase() : String(opt.value);
					const existing = byId.get(id);
					if (!existing) {
						byId.set(id, opt);
						continue;
					}
					const existingIsFriendly = existing.value === existing.label;
					const nextIsFriendly = opt.value === opt.label;
					if (!existingIsFriendly && nextIsFriendly) {
						byId.set(id, opt);
					}
				}
				const deduped = Array.from(byId.values());

				setConditionFieldOptions(deduped);
			} catch (e) {
				// fallback stays as-is
				console.error('Failed to load condition fields:', e);
			}
		})();

		return () => {
			active = false;
		};
	}, [currentWebsiteId, isMagento, storeView?.value, defaultStoreView]);

	// If we started with only static fields, but API later loads facet fields,
	// move any "Price" default condition over to the first available facet field.
	// Skip this migration if we're in edit mode or if Price field is already configured.
	useEffect(() => {
		// Don't migrate Price fields when editing existing rules
		if (isEditMode) return;
		
		const firstFacet = conditionFieldOptions.find((o) => o?.facetField);
		if (!firstFacet) return;

		setConditions((prev) =>
			prev.map((group) =>
				group.map((cond) => {
					if (cond?.field?.facetField) return cond;
					if (cond?.field?.value !== 'Price') return cond;
					
					// Skip Price fields that are already configured (have minValue/maxValue)
					// These are real Price conditions, not default placeholders
					if (cond?.minValue || cond?.maxValue) return cond;
					
					return {
						...cond,
						field: firstFacet,
						operator: { value: 'is one of', label: 'One of' },
						values: [],
						minValue: '',
						maxValue: ''
					};
				})
			)
		);
	}, [conditionFieldOptions, isEditMode]);

	const getFacetFieldOptions = async (facetField, inputValue) => {
		const payload = {
			websiteId: currentWebsiteId,
			q: inputValue,
			// send both keys to be compatible with possible backend naming
			facetField,
			field: facetField,
			...(isMagento && (storeView?.value || defaultStoreView)
				? { storeView: storeView?.value || defaultStoreView }
				: {}),
		};

	let response = await PostData("ms1", "api/v1/typesense/getSearchFacetField", payload);
	if (response !== "Invalid" && response?.status === "success") {
		const raw =
			response?.data?.facetFields?.[facetField] ??
			response?.data?.searchResult ??
			response?.data?.result ??
			response?.data?.data ??
			response?.data ??
			[];

		if (!Array.isArray(raw)) return [];
		return raw.map((x) => normalizeFacetOption(x, facetField)).filter(Boolean);
	}

		return [];
	};

	const getDefaultFacetOptions = (facetField) => {
		if (!facetField) return undefined;
		const raw = facetFieldsMap?.[facetField];
		if (!Array.isArray(raw)) return undefined;
		return raw.map((x) => normalizeFacetOption(x, facetField)).filter(Boolean);
	};

	const getSearchQuery = async (inputValue) => {
		const payload = {
			websiteId: currentWebsiteId,
			q: inputValue,
			...(isMagento && (storeView?.value || defaultStoreView)
				? { storeView: storeView?.value || defaultStoreView }
				: {}),
		};

		const response = await PostData("ms1", "api/v1/typesense/getSearchQuery", payload);
		if (response !== "Invalid" && response?.status === "success") {
			const raw =
				response?.data?.searchResult ??
				response?.data?.result ??
				response?.data?.data ??
				response?.data ??
				[];

			if (!Array.isArray(raw)) return [];
			return raw.map((item) => {
				// Normalize to { value, label } format
				if (typeof item === 'object' && item.value != null && item.label != null) {
					return { value: item.value, label: item.label };
				}
				// If it's a string or primitive, use it as both value and label
				return { value: item, label: String(item) };
			});
		}

		return [];
	};
	const getCollectionQuery = async (inputValue) => {
		const payload = {
			websiteId: currentWebsiteId,
			q: inputValue,
			...(isMagento && (storeView?.value || defaultStoreView)
				? { storeView: storeView?.value || defaultStoreView }
				: {}),
		};

		const response = await PostData("ms1", "api/v1/typesense/getSearchCollection", payload);
		if (response !== "Invalid" && response?.status === "success") {
			const raw =
				response?.data?.searchResult ??
				response?.data?.result ??
				response?.data?.data ??
				response?.data ??
				[];

			if (!Array.isArray(raw)) return [];
			return raw.map((item) => normalizeFacetOption(item, 'collections')).filter(Boolean);
		}

		return [];
	};

	const handleSearchQuery = async (inputValue) => {
		const selectedQuery = { value: inputValue, label: inputValue };
		setSelectedQueries((prev) => [...prev, selectedQuery])
	}

	const debouncedSearchQuery = useCallback(
		debounce((inputValue, callback) => {
			getSearchQuery(inputValue).then(callback);
		}, 500),
		[currentWebsiteId, isMagento, storeView?.value, defaultStoreView]
	);

	const debouncedCollectionQuery = useCallback(
		debounce((inputValue, callback) => {
			getCollectionQuery(inputValue).then(callback);
		}, 500),
		[currentWebsiteId, isMagento, storeView?.value, defaultStoreView]
	);

	// Action type options
	const actionTypeOptions = [
		{ value: 'promote', label: 'Promote' },
		{ value: 'bury', label: 'Bury' },
		{ value: 'hide', label: 'Hide' },
		{value:'Redirect',label:'Redirect'}
	];

	// Action value options for promote (1-100)
	const actionValueOptions = Array.from({ length: 100 }, (_, i) => ({
		value: (100 - i).toString(),
		label: (100 - i).toString()
	}));

	// Condition operator options
	const conditionOperatorOptions = [
		{ value: 'is one of', label: 'is one of' },
		{ value: 'contains', label: 'contains' },
		{ value: 'equals', label: 'equals' },
		{ value: 'to', label: 'to' }
	];

	// Category, Brand, and Tag-specific operator options
	const categoryOperatorOptions = [
		{ value: 'is one of', label: 'One of' },
		{ value: 'not', label: 'Not' }
	];

	const createdAtOperatorOptions	= [
		{value: "Within last", label: "Within last"},
		{value: "Older than", label: "Older than"}
	]

	// Price-specific operator options
	const priceOperatorOptions = [
		{ value: 'range', label: 'Range' },
		{ value: 'greater than', label: 'Greater than' },
		{ value: 'less than', label: 'Less than' },
		{ value: 'equal to', label: 'Equal to' },
		{ value: 'not equal to', label: 'Not equal to' }
	];


	const [conditions, setConditions] = useState([[
		{
			field: { value: 'Price', label: 'Price' },
			operator: { value: 'range', label: 'Range' },
			values: '', // string for non-facet fields
			minValue: '',
			maxValue: '',
			logic: 'AND',
			selectedLogic: null // Track which logic type is selected for this condition
		}
	]]);

	const toggleSection = (section) => {
		setExpandedSections(prev => ({
			...prev,
			[section]: !prev[section]
		}));
	};

	const resetDates = () => {
		setStartDate(null);
		setEndDate(null);
	};


	// URL validation function
	const isValidUrl = (url) => {
		if (!url || url.trim() === '') {
			return false;
		}
		
		const trimmedUrl = url.trim();
		
		// Check if URL is too short (less than 3 characters)
		if (trimmedUrl.length < 3) {
			return false;
		}
		
		// Add https:// if no protocol is provided
		let urlToCheck = trimmedUrl;
		if (!urlToCheck.startsWith('http://') && !urlToCheck.startsWith('https://')) {
			urlToCheck = 'https://' + urlToCheck;
		}
		
		try {
			const urlObj = new URL(urlToCheck);
			// Check if it's a valid web protocol
			if (urlObj.protocol !== 'http:' && urlObj.protocol !== 'https:') {
				return false;
			}
			// Check if hostname is valid (at least has a dot or is localhost)
			const hostname = urlObj.hostname;
			if (!hostname || hostname.length < 3) {
				return false;
			}
			// Check if hostname has at least one dot (for domain) or is localhost
			if (!hostname.includes('.') && hostname !== 'localhost') {
				return false;
			}
			return true;
		} catch {
			return false;
		}
	};

	const addCondition = (logic = 'AND') => {
		setSelectedOuterLogic(logic); // Set the selected outer logic type
		const defaultField =
			conditionFieldOptions.find((o) => o.facetField) ||
			conditionFieldOptions.find((o) => o.value === 'Price') ||
			conditionFieldOptions[0] ||
			{ value: 'Price', label: 'Price' };
		const newCondition = [{
			id: Date.now(),
			field: defaultField,
			operator: defaultField?.facetField ? { value: 'is one of', label: 'One of' } : { value: 'range', label: 'Range' },
			values: defaultField?.facetField ? [] : '',
			minValue: '',
			maxValue: '',
			logic: logic,
			selectedLogic: null
		}];
		setConditions([...conditions, newCondition]);
	};

	const removeCondition = (index) => {
		let conditionsLocal	= JSON.parse(JSON.stringify(conditions))
		conditionsLocal.splice(index, 1)
		setConditions(conditionsLocal);
		if( conditionsLocal.length === 1 ){
			setSelectedOuterLogic(null)
		}

	}

	const addConditionRow = (index, logic = 'AND') => {
		let conditionsLocal	= JSON.parse(JSON.stringify(conditions))
		conditionsLocal[index][0].selectedLogic	= logic
		const defaultField =
			conditionFieldOptions.find((o) => o.facetField) ||
			conditionFieldOptions.find((o) => o.value === 'Price') ||
			conditionFieldOptions[0] ||
			{ value: 'Price', label: 'Price' };
		conditionsLocal[index].push({
			id: Date.now(),
			field: defaultField,
			operator: defaultField?.facetField ? { value: 'is one of', label: 'One of' } : { value: 'range', label: 'Range' },
			values: defaultField?.facetField ? [] : '',
			minValue: '',
			maxValue: '',
			logic: logic,
			selectedLogic: logic
		})
		setConditions(conditionsLocal)
	}

	const removeSubCondition = (index, subIndex) => {
		let conditionsLocal	= JSON.parse(JSON.stringify(conditions))
		conditionsLocal[index].splice(subIndex, 1)
		if( conditionsLocal[index].length === 1 ){
			conditionsLocal[index][0].selectedLogic	= null
		}
		setConditions(conditionsLocal);
	};

	const updateSubCondition = (index, subIndex, field, value) => {
		let conditionsLocal	= JSON.parse(JSON.stringify(conditions));
		if( field === "field" ){
			const isPrice = value?.value?.toLowerCase() === 'price';
			const isCreatedAt = value?.value === 'Created at';
			const isFacetField = Boolean(value?.facetField);

			if (isFacetField) {
				conditionsLocal[index][subIndex].operator = { value: 'is one of', label: 'One of' };
			} else if (isCreatedAt) {
				conditionsLocal[index][subIndex].operator = { value: 'Within last', label: 'Within last' };
			} else if (isPrice) {
				conditionsLocal[index][subIndex].operator = { value: 'range', label: 'Range' };
			} else {
				conditionsLocal[index][subIndex].operator = { value: 'equals', label: 'equals' };
			}

			conditionsLocal[index][subIndex].values = isFacetField ? [] : '';
			conditionsLocal[index][subIndex].minValue	= ''
			conditionsLocal[index][subIndex].maxValue	= ''
		} else if (field === "operator" && conditionsLocal[index][subIndex].field?.value === 'Price') {
			// Handle Price operator change
			const isRangeOperator = value?.value === 'range';
			const currentOperator = conditionsLocal[index][subIndex].operator?.value;
			const currentIsRange = currentOperator === 'range';
			
			if (!isRangeOperator && currentIsRange) {
				// Switching from range to single-value: keep minValue, clear maxValue
				conditionsLocal[index][subIndex].maxValue = '';
			} else if (isRangeOperator && !currentIsRange) {
				// Switching from single-value to range: use minValue for both
				if (conditionsLocal[index][subIndex].minValue) {
					conditionsLocal[index][subIndex].maxValue = conditionsLocal[index][subIndex].minValue;
				}
			}
		}
		conditionsLocal[index][subIndex][field]	= value
		setConditions(conditionsLocal)
	}

	const goBack = () => {
		navigate('/search/merchandising/rules');
	};

	const handleSave = () => {
		// Validate dates before saving (only if both are provided)
		if (startDate && endDate) {
			if (startDate >= endDate) {
				toast.error('Start date must be before end date');
				return;
			}
		} else if ((startDate && !endDate) || (!startDate && endDate)) {
			toast.error('Please provide both start and end dates, or leave both empty');
			return;
		}

		// Validate redirect URL if Redirect is selected
		if (actionType.value === 'Redirect' && !isValidUrl(redirectUrl)) {
			toast.error('Please enter a valid URL');
			return;
		}

		// Collect all form data
		setSaveLoader(true)
		
		// Ensure Price (Values) fields have facetField before saving (like Color does)
		// IMPORTANT: Only affects Price fields with values array, NOT static Price with minValue/maxValue
		const preparedConditions = actionType.value === 'Redirect' ? [] : conditions.map((group) =>
			group.map((cond) => {
				const isPriceField = cond.field?.value?.toLowerCase() === 'price' || 
									 cond.field?.label?.toLowerCase().includes('price');
				const hasValuesArray = Array.isArray(cond.values) && cond.values.length > 0;
				const hasMinMaxValue = cond.minValue || cond.maxValue;
				
				// Only add facetField if:
				// 1. It's a Price field
				// 2. Has values array (Price Values)
				// 3. Does NOT have minValue/maxValue (not static Price)
				// 4. Missing facetField
				if (isPriceField && hasValuesArray && !hasMinMaxValue && !cond.field?.facetField) {
					return {
						...cond,
						field: {
							...cond.field,
							facetField: 'price'
						}
					};
				}
				// Static Price field (with minValue/maxValue) - return as-is, no changes
				return cond;
			})
		);
		
		const data = {
			websiteId: currentWebsiteId,
			...(isMagento && (storeView?.value || defaultStoreView)
				? { storeView: storeView?.value || defaultStoreView }
				: {}),
			scopeConfig: {
				searchResults: searchResults,
				smartCollections: smartCollections,
				scopeSelection: scopeSelection,
				collectionsSelection: collectionsSelection,
				selectedQueries: selectedQueries,
				selectedCollections: selectedCollections
			},
			actionConfig: {
				type: actionType.value,
				value: actionType.value === 'Redirect' ? 
					(redirectUrl.startsWith('http://') || redirectUrl.startsWith('https://') ? 
						redirectUrl : 'https://' + redirectUrl) : 
					actionValue.value
			},
			conditions: preparedConditions,
			// Add date object if both dates are provided
			...(startDate && endDate && {
				date: {
					startDate: startDate.toISOString().split('T')[0],
					endDate: endDate.toISOString().split('T')[0]
				}
			})
		};

		// Add ruleId for edit mode
		if (isEditMode) {
			data.ruleId = ruleId;
		}

		const apiEndpoint = isEditMode ? "api/v1/rule/editRule" : "api/v1/rule/create";
		
		PostData("ms1", apiEndpoint, data).then(response => {
			if( response !== "Invalid" ){
				if( response.status === "success" ){
					toast.success(response.message)
					navigate('/search/merchandising/rules');
				}else if( response.status === "failure" ){
					toast.error(response.message)
				}else{
					toast.error(response.message.message)
				}
			}
			setSaveLoader(false)
		})		
	};

	const handleCancel = () => {
		navigate('/search/merchandising/rules');
	};

	// Validation function to check if all required fields are filled
	const isFormValid = () => {
		// For Redirect action, skip conditions validation
		const hasValidConditions = actionType.value === 'Redirect' || conditions.every(group =>
			group.every(cond =>
			  (cond.values && cond.values.length > 0) ||
			  cond.minValue !== "" ||
			  cond.maxValue !== ""
			)
		 )

	// Check if action value is filled (for non-Redirect actions) or redirect URL is valid (for Redirect)
	const hasActionValue = actionType.value === 'Redirect' ? 
		isValidUrl(redirectUrl) : 
		(actionValue && actionValue.value != null && String(actionValue.value).trim() !== '');

		// Check if queries are selected when not using "All Queries"
		const hasValidQueries = scopeSelection === 'allQueries' || (Array.isArray(selectedQueries) && selectedQueries.length > 0);

		// Check if collections are selected when not using "All Collections"
		const hasValidCollections = collectionsSelection === 'allCollections' || (Array.isArray(selectedCollections) && selectedCollections.length > 0);

		// For Redirect action, at least one of Search Results or Smart Collections must be active
		const hasValidScopeForRedirect = actionType.value !== 'Redirect' || 
			(searchResults && (scopeSelection === 'allQueries' || (Array.isArray(selectedQueries) && selectedQueries.length > 0))) ||
			(smartCollections && (collectionsSelection === 'allCollections' || (Array.isArray(selectedCollections) && selectedCollections.length > 0)));

		// Note: Date validation is handled in handleSave() but doesn't disable the button
		// Users can save with any date combination (empty, partial, or complete)

		// For Redirect, use the combined scope validation instead of individual validations
		if (actionType.value === 'Redirect') {
			return hasValidConditions && hasActionValue && hasValidScopeForRedirect;
		}

		// For other actions, use the original validation
		return hasValidConditions && hasActionValue && hasValidQueries && hasValidCollections;
	};

	const SectionHeader = ({ title, expanded, onToggle, info }) => (
		<div className="d-flex justify-content-between align-items-center py-3 border-bottom">
			<h3 className="mb-0 font-weight-medium text-dark">{title}</h3>
			<div className="d-flex align-items-center gap-2">
				{info && (
					<Button className="btn-link p-0 text-primary">
						<FontAwesomeIcon icon={faInfoCircle} size="sm" />
					</Button>
				)}
				<Button onClick={onToggle} className="btn-link p-0 text-primary">
					<FontAwesomeIcon icon={expanded ? faChevronUp : faChevronDown} size="sm" />
				</Button>
			</div>
		</div>
	);

	const Toggle = ({ enabled, onChange, label }) => (
		<div className="d-flex justify-content-between align-items-center py-2">
			<span className="text-muted" style={{ fontSize: '16px', fontWeight: '500' }}>{label}</span>
			<div className="form-check form-switch">
				<input
					className="form-check-input"
					type="checkbox"
					checked={enabled}
					onChange={() => onChange(!enabled)}
					style={{ transform: 'scale(1.5)' }}
				/>
			</div>
		</div>
	);

	const RadioGroup = ({ options, value, onChange, name }) => (
		<div className="mb-3">
			{options.map((option) => (
				<div key={option.value} className="form-check">
					<input
						type="radio"
						name={name}
						value={option.value}
						checked={value === option.value}
						onChange={(e) => onChange(e.target.value)}
						className="form-check-input"
						id={`${name}-${option.value}`}
					/>
					<label className="form-check-label" htmlFor={`${name}-${option.value}`}>
						{option.label}
					</label>
				</div>
			))}
		</div>
	);

	return (
		<div className="merchandisingCategoriesWrapper">
			{/* Header */}
			{pageLoader ?
				<DetailSkeleton />
				:
				<React.Fragment>
					<div className="headerWrapper">
						<div className="categoriesHeader d-flex justify-content-between align-items-center">
							<Button onClick={goBack} style={{ background: "transparent", color: "#333" }}>
								<FontAwesomeIcon icon={faAngleLeft} />
								<FontAwesomeIcon icon={faGavel} />
								{isEditMode ? 'Edit Rule' : 'Rule Editor'}
							</Button>
							{/* <Button 
						onClick={() => setShowPreview(!showPreview)}
						className="btn preview-toggle-btn"
					>
						<FontAwesomeIcon icon={faDesktop} className="me-2" />
						{showPreview ? 'Hide Preview' : 'Show Preview'}
					</Button> */}
						</div>
					</div>
					<div className="categoriesContent">
						<div className="row">
							<div className={showPreview ? "col-lg-7" : "col-12"}>
								<div className="card">
									<div className="card-body">

										{/* Scope Section */}
										<div className="mb-4">
											<SectionHeader
												title="Scope"
												expanded={expandedSections.scope}
												onToggle={() => toggleSection('scope')}
											/>
											{expandedSections.scope && (
												<div className="py-3">
													<Toggle
														enabled={searchResults}
														onChange={setSearchResults}
														label="Search Results"
													/>
													{searchResults && (
														<div className="ml-4">
															<RadioGroup
																options={[
																	{ value: 'allQueries', label: 'All Queries' },
																	{ value: 'queries', label: 'Queries' },
																	{ value: 'excludeSelection', label: 'Exclude Selection' }
																]}
																value={scopeSelection}
																onChange={setScopeSelection}
																name="scope"
															/>
															{scopeSelection !== 'allQueries' && (
																<React.Fragment>
																	<AsyncCreatableSelect
																		isMulti
																		isClearable
																		cacheOptions
																		key={"searchQueries"}
																		value={selectedQueries}
																		onChange={setSelectedQueries}
																		defaultOptions // show some options initially
																		loadOptions={debouncedSearchQuery}
																		onCreateOption={async (inputValue) => {
																			const newOption = await handleSearchQuery(inputValue);
																			if (newOption) {
																				// optionally, you can set state or handle selection here
																				console.log("Created:", newOption);
																			}
																		}}
																		placeholder="Search or create query..."
																	/>
																</React.Fragment>
															)}
														</div>
													)}

													<Toggle
														enabled={smartCollections}
														onChange={setSmartCollections}
														label="Smart Collections"
													/>
													{smartCollections && (
														<div className="ml-4">
															<RadioGroup
																options={[
																	{ value: 'allCollections', label: 'All Collections' },
																	{ value: 'collections', label: 'Collections' },
																	{ value: 'excludeSelection', label: 'Exclude Selection' }
																]}
																value={collectionsSelection}
																onChange={setCollectionsSelection}
																name="collections"
															/>
															{collectionsSelection !== 'allCollections' && (
																<React.Fragment>
																	<AsyncSelect
																		isMulti
																		isClearable
																		cacheOptions
																		key={"searchCollection"}
																		value={selectedCollections}
																		onChange={setSelectedCollections}
																		defaultOptions // show some options initially
																		loadOptions={debouncedCollectionQuery}
																		placeholder="Search collection..."
																	/>
																</React.Fragment>
															)}
														</div>
													)}
												</div>
											)}
										</div>
										{/* Date Range Section */}
										<div className="mb-4">
											<SectionHeader
												title="Date"
												expanded={expandedSections.dates || false}
												onToggle={() => toggleSection('dates')}
											/>
										{expandedSections.dates && (
											<div className="py-3">
												<div className="d-flex justify-content-between align-items-center mb-3">
													<span className="text-muted small">Optional: Set date range for rule activation</span>
													{(startDate || endDate) && (
														<Button
															size="sm"
															color="link"
															onClick={resetDates}
															style={{ padding: '4px 8px', fontSize: '12px', textDecoration: 'none' }}
														>
															<FontAwesomeIcon icon={faTimes} className="me-1" />
															Clear Dates
														</Button>
													)}
												</div>
												<div className="row g-3">
														<div className="col-md-4">
															<label className="form-label">Start Date</label>
															<DatePicker
																selected={startDate}
																onChange={(date) => setStartDate(date)}
																dateFormat="yyyy-MM-dd"
																placeholderText="Select start date"
																maxDate={endDate ? new Date(endDate.getTime() - 24 * 60 * 60 * 1000) : null}
																showPopperArrow={false}
																popperClassName="custom-datepicker-popper"
																calendarClassName="custom-datepicker-calendar"
																customInput={
																	<input
																		type="text"
																		className="form-control"
																		placeholder="Select start date"
																		readOnly
																		onKeyDown={(e) => e.preventDefault()}
																		onPaste={(e) => e.preventDefault()}
																		onInput={(e) => e.preventDefault()}
																		style={{ paddingRight: '2.5rem', cursor: 'pointer' }}
																	/>
																}
																wrapperClassName="date-picker-wrapper"
															/>
														</div>
														<div className="col-md-4">
															<label className="form-label">End Date</label>
															<DatePicker
																selected={endDate}
																onChange={(date) => setEndDate(date)}
																dateFormat="yyyy-MM-dd"
																placeholderText="Select end date"
																minDate={startDate ? new Date(startDate.getTime() + 24 * 60 * 60 * 1000) : null}
																showPopperArrow={false}
																popperClassName="custom-datepicker-popper"
																calendarClassName="custom-datepicker-calendar"
																customInput={
																	<input
																		type="text"
																		className="form-control"
																		placeholder="Select end date"
																		readOnly
																		onKeyDown={(e) => e.preventDefault()}
																		onPaste={(e) => e.preventDefault()}
																		onInput={(e) => e.preventDefault()}
																		style={{ paddingRight: '2.5rem', cursor: 'pointer' }}
																	/>
																}
																wrapperClassName="date-picker-wrapper"
															/>
														</div>
													</div>
												</div>
											)}
										</div>
										{/* Action Section */}
										<div className="mb-4">
											<SectionHeader
												title="Action"
												expanded={expandedSections.action}
												onToggle={() => toggleSection('action')}
											/>
											{expandedSections.action && (
												<div className="py-3">
													<div className="row g-2">
														<div className="col-3">
															<Select
																value={actionType}
																onChange={setActionType}
																options={actionTypeOptions}
																isSearchable={false}
																className="react-select-container"
																classNamePrefix="react-select"
															/>
														</div>
														{actionType.value === 'promote' && (
															<div className="col-2">
																<Select
																	value={actionValue}
																	onChange={setActionValue}
																	options={actionValueOptions}
																	isSearchable={false}
																	className="react-select-container"
																	classNamePrefix="react-select"
																/>
															</div>
														)}
														{actionType.value === 'promote' && (
															<div className="col-1">
																<Button
																	className="btn btn-link p-0 action-info-btn"
																	type="button"
																	title="Select a value from 1-100 for promotion"
																>
																	<FontAwesomeIcon icon={faInfoCircle} size="sm" />
																</Button>
															</div>
														)}
														{actionType.value === 'Redirect' && (
															<div className="col-6">
																<input
																	type="url"
																	className="form-control"
																	placeholder="Enter redirect URL"
																	value={redirectUrl}
																	onChange={(e) => setRedirectUrl(e.target.value)}
																/>
															</div>
														)}
													</div>
												</div>
											)}
										</div>

										{/* Conditions Section - Hidden for Redirect action */}
										{actionType.value !== 'Redirect' && (
											<div className="mb-4">
												<SectionHeader
													title="Conditions"
													expanded={expandedSections.conditions}
													onToggle={() => toggleSection('conditions')}
												/>
												{expandedSections.conditions && (
												<div className="py-3">
													{/* Store View (Magento only) */}
													{isMagento && (
														<div className="row g-2 mb-3">
															<div className="col-md-4">
																<label className="form-label mb-1">Store View</label>
																<Select
																	value={storeView}
																	onChange={setStoreView}
																	options={storeViewOptions}
																	isLoading={!!magentoStoreViewsLoader}
																	isDisabled={!!magentoStoreViewsLoader || !storeViewOptions?.length}
																	isSearchable={false}
																	placeholder={magentoStoreViewsLoader ? "Loading store views..." : "Select store view"}
																	className="react-select-container"
																	classNamePrefix="react-select"
																	styles={{
																		control: (provided) => ({
																			...provided,
																			minHeight: '38px',
																			height: '38px'
																		}),
																		valueContainer: (provided) => ({
																			...provided,
																			height: '38px',
																			padding: '0 8px'
																		}),
																		input: (provided) => ({
																			...provided,
																			margin: '0px'
																		}),
																		indicatorsContainer: (provided) => ({
																			...provided,
																			height: '38px'
																		})
																	}}
																/>
															</div>
														</div>
													)}
													{conditions.map((innerCondition, index) => (														
														<div key={index} className="mb-3">
															{index > 0 && (
																<div className="d-flex justify-content-center mb-3">
																	<div className="d-flex align-items-center gap-2 condition-logic-badge">
																		<div className="condition-logic-icon">
																			+
																		</div>
																		{innerCondition[0].logic}
																	</div>
																</div>
															)}

															<div className="card position-relative">
																{/* Only show delete button if there's more than one condition */}
																{conditions.length > 1 && (
																	<Button
																		onClick={() => removeCondition(index)}
																		className="btn btn-link p-0 position-absolute rule-delete-btn"
																	>
																		<FontAwesomeIcon icon={faTimes} />
																	</Button>
																)}
																<div className="card-body">
																	{/* Render sub-conditions within the same card */}
																	{innerCondition.map((subCondition, subIndex) => (
																		<div key={subIndex} className="mt-3">
																			{subIndex > 0 &&
																				<div className="d-flex justify-content-center mb-2">
																					<div className="condition-badge">
																						<div className="condition-badge-icon">
																							+
																						</div>
																						{subCondition.selectedLogic || 'and/or'}
																					</div>
																				</div>
																			}
																			{/* Sub-condition row with all 3 input fields */}
																			<div className="row g-2 mb-1">
																				<div className="col-md-2">
																					<Select
																						value={subCondition.field}
																						onChange={(selectedOption) => updateSubCondition(index, subIndex, 'field', selectedOption)}
																						options={conditionFieldOptions}
																						isSearchable={true}
																						className="react-select-container"
																						classNamePrefix="react-select"
																						styles={{
																							control: (provided) => ({
																								...provided,
																								minHeight: '38px',
																								height: '38px'
																							}),
																							valueContainer: (provided) => ({
																								...provided,
																								height: '38px',
																								padding: '0 8px'
																							}),
																							input: (provided) => ({
																								...provided,
																								margin: '0px'
																							}),
																							indicatorsContainer: (provided) => ({
																								...provided,
																								height: '38px'
																							})
																						}}
																					/>
																				</div>

																				<div className="col-md-2">
																					<Select
																						value={subCondition.operator}
																						onChange={(selectedOption) => updateSubCondition(index, subIndex, 'operator', selectedOption)}
																						options={
																							subCondition.field.value === 'Price'
																								? priceOperatorOptions
																								: subCondition.field?.facetField
																									? categoryOperatorOptions
																									: subCondition.field.value === "Created at"
																										? createdAtOperatorOptions
																										: conditionOperatorOptions
																						}
																						isSearchable={false}
																						className="react-select-container"
																						classNamePrefix="react-select"
																						styles={{
																							control: (provided) => ({
																								...provided,
																								minHeight: '38px',
																								height: '38px'
																							}),
																							valueContainer: (provided) => ({
																								...provided,
																								height: '38px',
																								padding: '0 8px'
																							}),
																							input: (provided) => ({
																								...provided,
																								margin: '0px'
																							}),
																							indicatorsContainer: (provided) => ({
																								...provided,
																								height: '38px'
																							})
																						}}
																					/>
																				</div>

																				<div className={subCondition.field.value === 'Price' && !subCondition.field?.facetField ? "col-md-4" : "col-md-5"}>
																					{subCondition.field?.facetField ? (
																						// Any facet field (including Price Values) - show AsyncSelect dropdown
																						<div className="mb-2">
																							<div className="d-flex align-items-center gap-2">
																								<div className="flex-grow-1">
																									<AsyncSelect
																										isMulti
																										isClearable
																										cacheOptions
																									defaultOptions={getDefaultFacetOptions(subCondition.field.facetField) ?? true}
																										value={Array.isArray(subCondition.values) ? subCondition.values : []}
																										onChange={(selectedOption) => updateSubCondition(index, subIndex, "values", selectedOption)}
																										loadOptions={(inputValue) => getFacetFieldOptions(subCondition.field.facetField, inputValue)}
																										placeholder={`Search ${subCondition.field.label || subCondition.field.value}...`}
																									/>
																								</div>
																							</div>
																						</div>
																					) : subCondition.field.value === 'Price' && !subCondition.field?.facetField ? (
																						// Static Price field (no facetField) - show numeric inputs
																						subCondition.operator?.value === 'range' ? (
																							<div className="input-group custom-price-input-group">
																								<input
																									type="number"
																									value={subCondition.minValue || ''}
																									onChange={(e) => updateSubCondition(index, subIndex, 'minValue', e.target.value)}
																									className="form-control custom-price-input"
																								/>
																								<span className="input-group-text custom-price-separator">to</span>
																								<input
																									type="number"
																									value={subCondition.maxValue || ''}
																									onChange={(e) => updateSubCondition(index, subIndex, 'maxValue', e.target.value)}
																									className="form-control custom-price-input"
																								/>
																							</div>
																						) : (
																							<input
																								type="number"
																								value={subCondition.minValue || ''}
																								onChange={(e) => updateSubCondition(index, subIndex, 'minValue', e.target.value)}
																								className="form-control"
																								placeholder="Enter price"
																							/>
																						)
																					) : subCondition.field.value === "Created at" ? (
																						<div className="d-flex gap-2 align-items-center">
																							<input
																								type="text"
																								value={typeof subCondition.values === 'string' ? subCondition.values : ''}
																								onChange={(e) => updateSubCondition(index, subIndex, 'values', e.target.value)}
																								className="form-control"
																								placeholder="Enter value"
																							/>
																							<span className='text-grey' style={{ color: "#ccc" }}>days</span>
																						</div>
																					) : (
																						<input
																							type="text"
																							value={typeof subCondition.values === 'string' ? subCondition.values : ''}
																							onChange={(e) => updateSubCondition(index, subIndex, 'values', e.target.value)}
																							className="form-control"
																							placeholder="Enter value"
																						/>
																					)}
																				</div>
																				{/* Dustbin icon at the end of the row */}
																				<div className="col-auto d-flex align-items-center">
																					{innerCondition.length > 1 &&
																						<Button
																							onClick={() => removeSubCondition(index, subIndex)}
																							className="btn btn-link p-0 sub-condition-delete-btn"
																						>
																							<FontAwesomeIcon icon={faTrash} />
																						</Button>
																					}
																				</div>
																			</div>
																		</div>
																	))}

																</div>

																{/* AND/OR buttons at the very bottom of each condition card */}
																{/* Second row - AND/OR buttons */}
																<div className="row mb-3 ms-1">
																	<div className="col-12">
																		<div className="d-flex gap-2">
																			{/* Show both buttons initially, or only the selected logic type */}
																			{(!innerCondition[0].selectedLogic || innerCondition[0].selectedLogic === 'AND') && (
																				<Button
																					onClick={() => addConditionRow(index, 'AND')}
																					className="btn logic-button"
																				>
																					<div className="logic-button-icon">
																						+
																					</div>
																					AND
																				</Button>
																			)}
																			{(!innerCondition[0].selectedLogic || innerCondition[0].selectedLogic === 'OR') && (
																				<Button
																					onClick={() => addConditionRow(index, 'OR')}
																					className="btn logic-button"
																				>
																					<div className="logic-button-icon">
																						+
																					</div>
																					OR
																				</Button>
																			)}
																		</div>
																	</div>
																</div>
															</div>
														</div>
													))}
													<div className="d-flex justify-content-start gap-2 mt-3">
														{/* Show both buttons initially, or only the selected logic type */}
														{(!selectedOuterLogic || selectedOuterLogic === 'AND') && (
															<Button
																onClick={() => addCondition('AND')}
																className="btn logic-button"
															>
																<div className="logic-button-icon">
																	+
																</div>
																AND
															</Button>
														)}
														{(!selectedOuterLogic || selectedOuterLogic === 'OR') && (
															<Button
																onClick={() => addCondition('OR')}
																className="btn logic-button"
															>
																<div className="logic-button-icon">
																	+
																</div>
																OR
															</Button>
														)}
													</div>
												</div>
											)}
										</div>
										)}

										{/* Save and Cancel buttons - Always visible */}
										<div className="mt-4 d-flex justify-content-end gap-2">
											<Button onClick={handleCancel} className="btn cancel-btn cta-outline autoWidth" outline>
												Cancel
											</Button>
											<Button
												onClick={handleSave}
												className="btn btn-primary save-btn cta autoWidth"
												disabled={!isFormValid() || saveLoader}
											>
												{saveLoader ? <><Spinner size="sm" /> {isEditMode ? "Updating..." : "Saving..."}</> : <>{isEditMode ? "Update" : "Save"}</>}
											</Button>
										</div>
									</div>
								</div>
							</div>

							{showPreview && (
								<div className="col-lg-5">
									<div className="card">
										<div className="card-header">
											<h5 className="mb-0">Preview</h5>
										</div>
										<div className="card-body">
											<div className="text-center text-muted">
												<FontAwesomeIcon icon={faInfoCircle} size="3x" className="mb-3" />
												<p>No preview available</p>
											</div>
										</div>
									</div>
								</div>
							)}
						</div>
					</div>
				</React.Fragment>
			}
		</div>
	);
};

export default RuleEditor;
